// Generic weighted table helper. Keeps game tuning data decoupled from gameplay code.
// This file is intentionally standalone (no imports) so it can be introduced safely.

export type WeightedItem<T> = {
  item: T;
  weight: number; // >= 0
};

export interface RngLike {
  /** Returns a float in [0, 1). */
  next(): number;
}

/**
 * WeightedTable is a small utility for picking an item based on weights.
 * It is deterministic if you pass a deterministic RNG.
 */
export class WeightedTable<T> {
  private readonly items: Array<WeightedItem<T>>;
  private readonly totalWeight: number;

  constructor(items: Array<WeightedItem<T>>) {
    if (!items.length) throw new Error("WeightedTable requires at least one item");
    this.items = items.map((x) => ({ item: x.item, weight: Math.max(0, x.weight) }));
    this.totalWeight = this.items.reduce((sum, x) => sum + x.weight, 0);
    if (this.totalWeight <= 0) throw new Error("WeightedTable total weight must be > 0");
  }

  /** Picks an item. If some weights are 0 they will never be chosen. */
  pick(rng: RngLike): T {
    // Clamp in case RNG returns 1.0 due to floating error.
    const r = Math.min(0.999999999, Math.max(0, rng.next()));
    let roll = r * this.totalWeight;
    for (const entry of this.items) {
      roll -= entry.weight;
      if (roll <= 0) return entry.item;
    }
    // Shouldn't happen, but fall back to last item for safety.
    return this.items[this.items.length - 1]!.item;
  }

  /** Useful for debugging/tuning screens. */
  toJSON(): Array<WeightedItem<T>> {
    return this.items.slice();
  }

  static single<T>(item: T): WeightedTable<T> {
    return new WeightedTable([{ item, weight: 1 }]);
  }
}
